import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import PhoneNumberValidator from '../../../validator/phone-number.validator';
import { ApiState } from '../../../model/api-state.model';
import * as fromSaveContact from '../../../store/save-contact/save-contact.reducer';
import { SelectOption } from '../../dynamic-input/dynamic-input.component';
import { SaveContactPayload } from '../../../model/save-contact.model';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { ProcessService } from '../../../services/process.service';
import { getFirstArrayValue, isFuture, isToday } from '../../../utils';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';
import { TrackingService } from '../../../tracking/tracking.service';
import { NX_MODAL_DATA } from '@aposin/ng-aquila/modal';
import FirstNameValidator from 'src/app/validator/firstNameValidator';
import LastNameValidator from 'src/app/validator/lastNameValidator';

interface ModalData {
  type: string;
}

@Component({
  selector: 'app-contact-agent',
  templateUrl: './contact-agent.component.html',
  styleUrls: ['./contact-agent.component.scss'],
})
export class ContactAgentComponent implements OnInit, OnDestroy {
  saveContactState$ = this.store.select(fromSaveContact.selectSaveContactState);
  saveContactError$ = this.store.select(fromSaveContact.selectSaveContactError);
  ngDestroy$ = new Subject<void>();

  formGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required, FirstNameValidator]),
    lastName: new FormControl('', [Validators.required, LastNameValidator]),
    phoneNumber: new FormControl('', [Validators.required, PhoneNumberValidator]),
    message: new FormControl('Wyczynowe uprawianie sportu'),
    immediateContact: new FormControl<'now' | 'planned' | null>(null, [Validators.required]),
    date: new FormControl('', [Validators.required]),
    time: new FormControl<string | null>(null, [Validators.required]),
  });

  immediateContactOptions: SelectOption[] = [
    { label: 'modals.contact-agent.form.immediateContact.options.now', value: 'now' },
    { label: 'modals.contact-agent.form.immediateContact.options.planned', value: 'planned' },
  ];

  timeOptions: SelectOption[] = [
    { label: '08:00 - 11:00', value: '08:00' },
    { label: '11:00 - 15:00', value: '11:00' },
    { label: '15:00 - 18:00', value: '15:00' },
    { label: '18:00 - 20:00', value: '18:00' },
  ];
  contentType: string | null = null;
  filteredTimeOptions$ = new BehaviorSubject<SelectOption[]>(this.timeOptions);

  protected readonly apiStates = ApiState;

  constructor(
    private store: Store<AppState>,
    private processService: ProcessService,
    private trackingService: TrackingService,
    @Inject(NX_MODAL_DATA) readonly data: ModalData,
  ) {
    this.contentType = data?.type ? data.type : null;
    this.formGroup.controls.date.valueChanges.pipe(takeUntil(this.ngDestroy$)).subscribe((date) => {
      let filteredTimeOpts = this.timeOptions;

      if (date && isToday(date)) {
        filteredTimeOpts = this.timeOptions.filter(({ value }) => isFuture(value));
      }

      this.filteredTimeOptions$.next(filteredTimeOpts);
    });
  }

  get minDate(): string | undefined {
    const now = new Date();

    return getFirstArrayValue(now.toISOString().split('T')) || undefined;
  }

  ngOnInit(): void {
    this.store.dispatch(fromSaveContact.actions.reset());
    this.implementFormValidation();
    this.implementTracking();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  handleSubmit(): void {
    const payload: SaveContactPayload = {
      firstName: this.formGroup.value.firstName ?? '',
      lastName: this.formGroup.value.lastName ?? '',
      phoneNumber: this.formGroup.value.phoneNumber?.replace(/-/g, '') ?? '',
      message: this.formGroup.value.message ?? `Kontakt z formularza na stronie ${location.href}`,
      immediateContact: this.formGroup.value.immediateContact === 'now' ?? false,
      preferredContactDate:
        this.formGroup.value.date && this.formGroup.value.time
          ? new Date(`${this.formGroup.value.date}T${this.formGroup.value.time ?? '00:00'}:00.000Z`)
          : new Date(),
      ...this.processService.apiPayload,
    };

    this.store.dispatch(fromSaveContact.actions.start({ payload }));
  }

  private implementFormValidation(): void {
    this.formGroup.controls.immediateContact.valueChanges.pipe(takeUntil(this.ngDestroy$)).subscribe((value) => {
      if (value === 'now') {
        this.formGroup.controls.date.setValidators([]);
        this.formGroup.controls.date.setValue(null);
        this.formGroup.controls.time.setValidators([]);
        this.formGroup.controls.time.setValue(null);
      } else {
        this.formGroup.controls.date.setValidators([Validators.required]);
        this.formGroup.controls.time.setValidators([Validators.required]);
      }

      this.formGroup.controls.date.updateValueAndValidity();
      this.formGroup.controls.time.updateValueAndValidity();
    });
  }

  private implementTracking(): void {
    this.trackingService.contactAgentOpen();

    this.saveContactState$
      .pipe(
        takeUntil(this.ngDestroy$),
        filter((state) => state === ApiState.success),
      )
      .subscribe(() => {
        this.trackingService.contactAgentSent();
      });
  }
}
